import { KnowledgeBasesV2Service } from "@/generated/sdk.gen";
import type {
  EnableSyncRequest,
  KbSyncSchedule,
  SyncStatusResponse,
  UpdateSyncRequest,
  WatchedItemSchema,
} from "@/generated/types.gen";

/** Next upcoming run for a sync, from the backend-computed ``next_runs``
 *  preview (same value the schedule_v2 picker surfaces). */
export function nextRunLabel(sync: { next_runs?: string[] }): string {
  const next = sync.next_runs?.[0];
  return next ? `${next} UTC` : "-";
}

/** Map config-style source keys to SourceType enum values expected by the API. */
const SOURCE_TYPE_MAP: Record<string, string> = {
  google_drive: "Google Drive",
  one_drive: "OneDrive",
  sharepoint: "SharePoint",
  website: "Website",
};

export function normalizeSourceType(sourceType: string): string {
  return SOURCE_TYPE_MAP[sourceType] || sourceType;
}

/** Human-readable display labels for source types. */
export function formatSourceTypeLabel(sourceType: string): string {
  const label = SOURCE_TYPE_MAP[sourceType];
  if (label) return label;
  // Handle API-cased values that are already display-ready
  const DISPLAY_MAP: Record<string, string> = {
    "Google Drive": "Google Drive",
    OneDrive: "OneDrive",
    Sharepoint: "SharePoint",
    Website: "Website",
    ServiceNow: "ServiceNow",
  };
  return DISPLAY_MAP[sourceType] || sourceType;
}

/**
 * Human-friendly label for a sync's Resource column. Normalized so
 * folders, files, and mixed selections all read the same way:
 * "<first-item-name> (+N)" when there are extras, just the name
 * otherwise. Folders are preferred as the lead item.
 *
 * Pickers sometimes return a flat list of files even when the user
 * selected a folder (they expand server-side), so we can't just use
 * `watched_items[0].name` blindly.
 */
export function formatSyncResourceLabel(sync: SyncStatusResponse): string {
  // Explicit label set at enable time (e.g. the folder name when the user
  // enabled sync on a folder, which expands to per-file watches) wins.
  if (sync.resource_label) return sync.resource_label;

  const items = sync.watched_items ?? [];
  if (items.length === 0) return formatSourceTypeLabel(sync.source_type);

  const firstFolder = items.find((i) => i.type === "folder");
  const lead = firstFolder ?? items[0];
  const others = items.length - 1;
  return others > 0 ? `${lead.name} (+${others})` : lead.name;
}

// Re-export generated types so call-sites can import from one place.
export type {
  EnableSyncRequest,
  KbSyncSchedule,
  SyncStatusResponse,
  UpdateSyncRequest,
  WatchedItemSchema as WatchedItem,
};

/**
 * Thin shim around the generated SDK that keeps the previous (groupId, kbId, ...)
 * call shape so existing hooks didn't have to change. New call-sites should
 * prefer ``KnowledgeBasesV2Service.*`` directly.
 */
export const KBSyncService = {
  listSyncs(groupId: string, kbId: string) {
    return KnowledgeBasesV2Service.listKbSyncs({
      path: { group_id: groupId, knowledge_base_id: kbId },
    });
  },

  enableSync(groupId: string, kbId: string, body: EnableSyncRequest) {
    return KnowledgeBasesV2Service.enableKbSync({
      path: { group_id: groupId, knowledge_base_id: kbId },
      body: {
        ...body,
        source_type: normalizeSourceType(
          body.source_type as unknown as string
        ) as EnableSyncRequest["source_type"],
      },
    });
  },

  updateSync(
    groupId: string,
    kbId: string,
    triggerId: string,
    body: UpdateSyncRequest
  ) {
    return KnowledgeBasesV2Service.updateKbSync({
      path: {
        group_id: groupId,
        knowledge_base_id: kbId,
        trigger_id: triggerId,
      },
      body,
    });
  },

  disableSync(groupId: string, kbId: string, triggerId: string) {
    return KnowledgeBasesV2Service.disableKbSync({
      path: {
        group_id: groupId,
        knowledge_base_id: kbId,
        trigger_id: triggerId,
      },
    });
  },

  forceSync(groupId: string, kbId: string, triggerId: string) {
    return KnowledgeBasesV2Service.forceKbSync({
      path: {
        group_id: groupId,
        knowledge_base_id: kbId,
        trigger_id: triggerId,
      },
    });
  },
};
